import { FC } from 'react'
import cls from '../styles/StaticContent.module.scss'
import { HeaderBar } from '@/components/HeaderBar'
import { GetServerSideProps } from 'next'
import { SEO } from '@/components/SEO'
import { Footer } from '@/components/Footer'
import { useTeamsMobile } from '@/hooks/useTeamsMobile'

interface SSP {
  insideTeams: boolean
}

/* eslint-disable */
const Help: FC<SSP> = ({ insideTeams }) => {
  const isTeamsMobile = useTeamsMobile()
  return (
    <>
      <SEO
        title="How to Use My Reminders for Microsoft Teams - Help Guide"
        description="Learn how to schedule, manage and use reminders in Microsoft Teams with our comprehensive help guide for the My Reminders app."
        keywords="Microsoft Teams reminders, Teams reminder help, schedule Teams reminders, manage Teams reminders"
      />
      <div className="flex flex-col min-h-screen">
        <HeaderBar showLogOutButton={false} className={insideTeams ? '!hidden' : ''} />
        <div className={`${cls.container} flex-grow`}>
          <h1>How to Use My Reminders for Microsoft Teams</h1>

          <p>
            <i>My Reminders for Microsoft Teams</i> is a Microsoft Teams app that lets you
            schedule reminders for later delivery. Essentially, you tell the app to remind
            you of something important, at a certain time.
          </p>

          <h2>How do I schedule a reminder?</h2>

          <p>
            In order to schedule a reminder, you have three options. You can use the chat
            compose extensions, the app dashboard, or our external website.
          </p>

          <h3>1) Chat compose extension</h3>
          <p>
            When you are in the desired Teams chat, click the <i>Reminders</i> icon below
            the chat box. This will bring up a popup view that lets you indicate what to say
            in your reminder, and when you want to be reminded.
          </p>

          <h3>2) App dashboard</h3>
          <p>
            If you open the "Apps" view in Microsoft Teams and select <i>Reminders</i>, you
            will see a <i>Dashboard</i> tab. In this view there is a button that lets you
            schedule reminders. You can indicate whether this reminder is related to a
            specific chat in Microsoft Teams. You need to write the content of your
            reminder, and its delivery time.
          </p>

          <h3>3) External web app</h3>
          <p>
            If you prefer so, you can open&nbsp;
            <a href="https://teamsreminder.com/app" target="_blank">
              https://teamsreminder.com/app
            </a>
            &nbsp;using your favorite browser. Once you login, you will see the same options
            as in the dashboard above.
          </p>

          <h2>How can I see, edit and cancel scheduled reminders?</h2>
          <p>
            You can open the <i>Reminders</i> dashboard or web app as described above. There
            you will see a sorted list of scheduled reminders, which you would be able to
            edit, reschedule or cancel as needed.
          </p>

          <h2>Can I attach files to my reminders?</h2>
          <p>
            {isTeamsMobile ? (
              "Yes! With premium features, you can attach files to your reminders. When creating or editing a reminder, you'll see a paperclip icon that allows you to upload attachments."
            ) : (
              "Yes! With a Pro or Team subscription, you can attach up to 10 files to your reminders. When creating or editing a reminder, you'll see a paperclip icon that allows you to upload attachments. We support common file types including images, PDFs, Word documents, Excel spreadsheets, and PowerPoint presentations."
            )}
          </p>

          <h2>Can I create recurring reminders?</h2>
          <p>
            {isTeamsMobile ? (
              "Yes! With premium features, you can set up recurring reminders that repeat on a schedule you define. When creating a reminder, you'll have options to set it to repeat on daily, weekly, monthly, or yearly intervals. This is perfect for regular check-ins, meetings, or routine tasks."
            ) : (
              "Yes! With a Pro or Team subscription, you can set up recurring reminders that repeat on a schedule you define. When creating a reminder, you'll have options to set it to repeat on daily, weekly, monthly, or yearly intervals. This is perfect for regular check-ins, meetings, or routine tasks."
            )}
          </p>

          <h2>How much does it cost?</h2>
          {isTeamsMobile ? (
            <>
              <p>
                <i>My Reminders for Microsoft Teams</i> offers a free version with optional
                premium features. To see detailed pricing and to upgrade your account,
                please visit our website from a desktop computer.
              </p>
            </>
          ) : (
            <>
              <p>
                <i>My Reminders for Microsoft Teams</i> offers both free and premium plans:
              </p>
              <ul>
                <li>
                  <strong>Basic (Free):</strong> Up to 5 active reminders, 7-day message history
                </li>
                <li>
                  <strong>Pro:</strong> Unlimited reminders, file attachments, recurring
                  reminders, 90-day message history
                </li>
                <li>
                  <strong>Team:</strong> Everything in Pro plus centralized billing, user
                  management, and priority support
                </li>
              </ul>
              <p>
                Visit our <a href="/pricing">pricing page</a> for more details and to upgrade
                your account.
              </p>
            </>
          )}

          <h2>Can I share some feedback?</h2>
          <p>
            Absolutely! We'd love to hear from you. Use our <a href="/contact-us">contact form</a> to send us your feedback, 
            feature requests, or report any issues you encounter.
          </p>
          <p>
            We value your input and are constantly working to improve the app based on user suggestions!
          </p>
        </div>
        <Footer />
      </div>
    </>
  )
}

export default Help

export const getServerSideProps: GetServerSideProps<SSP> = async (context) => {
  return {
    props: {
      insideTeams: !!context.query.teamsEmail,
    },
  }
}
